import { useEffect, useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import { OverlayScrollArea } from '../components/OverlayScrollbar'
import { providerClient } from '../provider/providerClient'
import type { ProviderSupplied, SuppliedFile, SuppliedNode } from '../provider/providerClient'

/**
 * WHAT YOU SENT US — the delivery ledger, base source #1, read-only.
 *
 * ★★ NO ANALYTICS COLUMNS, EVER (dashboard plan §6, the partition rule). The two sets are not 1:1
 * in either direction — one delivery becomes seven episodes; a master, its captions and its
 * artwork are three deliveries of one title — so a views column cannot hang off a delivered file.
 * The failure mode is TRUST, not cosmetics: anything delivered but not yet published would show
 * 0 views, which reads as "nobody watched my film" rather than "it isn't out yet".
 *
 * ★ WHAT IT DOES SHOW is the association: where a delivery has become a catalogue title, it says
 * so. That link is what v1 faked with a hand-authored `menu-live.json` per organization.
 *
 * ⚠ The ledger is ADMIN-AUTHORED and this portal cannot change it. "Something missing?" is the
 * whole write story — they tell us, we fix it.
 */

const wrap: CSSProperties = { padding: 24, maxWidth: 1000 }
/** ★ Primary:VeryDark, not --foreground — see the note in `Overview.tsx`. */
const h1: CSSProperties = {
  fontSize: 'var(--fs-title)',
  fontWeight: 600,
  margin: '0 0 2px',
  color: 'var(--primary-verydark)',
}
const sub: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)', margin: '0 0 18px' }
const muted: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-sm)' }
const tiny: CSSProperties = { color: 'var(--muted-foreground)', fontSize: 'var(--fs-xs)' }

const titleRow: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '9px 12px',
  borderBottom: '1px solid var(--border)',
  flexWrap: 'wrap',
}

const groupPanel: CSSProperties = {
  background: 'var(--panel)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius)',
  padding: '8px 12px 10px',
  marginBottom: 8,
}

const fileRow: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) 150px 110px',
  gap: 10,
  padding: '3px 0',
  fontSize: 'var(--fs-sm)',
  alignItems: 'center',
}

const pill: CSSProperties = {
  fontSize: 'var(--fs-xs)',
  color: 'var(--muted-foreground)',
  border: '1px solid var(--border)',
  borderRadius: 999,
  padding: '0 8px',
  whiteSpace: 'nowrap',
}

const linkPill: CSSProperties = { ...pill, color: 'var(--success)', borderColor: 'var(--success)' }

/** The four groups, in the order admin authors them. */
const BUCKETS: [string, string][] = [
  ['Media', 'Media files'],
  ['Artwork', 'Artwork'],
  ['Descriptive', 'Descriptive files'],
  ['Supporting', 'Supporting material'],
]

function day(value: number): string {
  const s = String(value)
  return s.length === 8 ? `${s.slice(6, 8)}/${s.slice(4, 6)}/${s.slice(0, 4)}` : ''
}

interface TreeNode extends SuppliedNode {
  depth: number
  children: TreeNode[]
}

/**
 * Flatten the server's parent-keyed list into display order.
 *
 * ⚠ An orphan (a parent that is not in the list) is promoted to the top rather than dropped —
 * silently losing a delivery from a supplier's own record is the one outcome this screen must
 * never produce.
 */
function flatten(nodes: SuppliedNode[]): TreeNode[] {
  const byKey = new Map<string, TreeNode>()
  for (const n of nodes) byKey.set(n.PrimaryKey, { ...n, depth: 0, children: [] })

  const roots: TreeNode[] = []
  for (const node of byKey.values()) {
    const parent = node.ParentKey ? byKey.get(node.ParentKey) : undefined
    if (parent) parent.children.push(node)
    else roots.push(node)
  }

  const out: TreeNode[] = []
  const walk = (list: TreeNode[], depth: number) => {
    list.sort((a, b) => a.SortOrder - b.SortOrder || a.Name.localeCompare(b.Name))
    for (const node of list) {
      node.depth = depth
      out.push(node)
      walk(node.children, depth + 1)
    }
  }
  walk(roots, 0)

  return out
}

function Files({ files }: { files: SuppliedFile[] }) {
  const groups = BUCKETS.map(([key, label]) => [label, files.filter((f) => (f.Bucket || 'Media') === key)] as const)
    .filter(([, rows]) => rows.length > 0)

  if (groups.length === 0) {
    // ⚠ Ordinary list furniture, not a detection: a title with nothing recorded against it yet is
    // a normal state, and saying so beats an empty space that looks like a rendering fault.
    return <div style={{ ...tiny, padding: '4px 12px 12px 34px' }}>Nothing recorded against this title yet.</div>
  }

  return (
    <div style={{ padding: '6px 12px 12px 34px' }}>
      {groups.map(([label, rows]) => (
        <div key={label} style={groupPanel}>
          <div style={{ ...tiny, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>{label}</div>
          {rows.map((f) => (
            <div key={f.PrimaryKey} style={fileRow}>
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.Name}>
                {f.Label || f.Name}
              </span>
              <span style={tiny} title={f.Name}>
                {f.Label ? f.Name : f.MediaType}
              </span>
              <span style={{ ...tiny, fontFamily: 'var(--font-number)', textAlign: 'right' }}>
                {day(f.UploadedDayInt)}
              </span>
            </div>
          ))}
        </div>
      ))}
    </div>
  )
}

export function SuppliedScreen() {
  const [data, setData] = useState<ProviderSupplied | null>(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState<Set<string>>(new Set())

  useEffect(() => {
    let cancelled = false
    providerClient
      .supplied()
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : 'Could not load your deliveries.')
      })
    return () => {
      cancelled = true
    }
  }, [])

  const rows = useMemo(() => flatten(data?.Nodes ?? []), [data])

  if (error) return <div style={wrap}><div style={{ ...muted, color: 'var(--destructive)' }}>{error}</div></div>
  if (!data) return <div style={wrap}><div style={muted}>Loading…</div></div>
  if (!data.Linked) {
    return (
      <div style={wrap}>
        <div style={muted}>
          Your account is not linked to an organization yet — email{' '}
          <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>info@yoreflix.com</a>.
        </div>
      </div>
    )
  }

  const files = data.Files ?? {}
  const fileCount = Object.values(files).reduce((n, list) => n + list.length, 0)

  return (
    <OverlayScrollArea axis="y">
      <div style={wrap}>
        <h1 style={h1}>What you sent us</h1>
        <p style={sub}>
          {rows.length} {rows.length === 1 ? 'title' : 'titles'} · {fileCount}{' '}
          {fileCount === 1 ? 'file recorded' : 'files recorded'}. This is our record of what has arrived — it is not a
          list of what is published.
        </p>

        <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
          {rows.length === 0 && (
            <div style={{ ...titleRow, borderBottom: 'none' }}>
              <span style={muted}>No deliveries recorded yet.</span>
            </div>
          )}
          {rows.map((node) => {
            const isOpen = open.has(node.PrimaryKey)
            const own = files[node.PrimaryKey] ?? []
            return (
              <div key={node.PrimaryKey}>
                <div style={{ ...titleRow, paddingLeft: 12 + node.depth * 18 }}>
                  <button
                    type="button"
                    className="admin-btn"
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${node.Name}`}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      padding: '2px 8px',
                      fontSize: 'var(--fs-sm)',
                      cursor: 'pointer',
                      color: 'var(--foreground)',
                    }}
                    onClick={() =>
                      setOpen((prev) => {
                        const next = new Set(prev)
                        if (next.has(node.PrimaryKey)) next.delete(node.PrimaryKey)
                        else next.add(node.PrimaryKey)
                        return next
                      })
                    }
                  >
                    {isOpen ? '▾' : '▸'}
                  </button>
                  <span style={{ flex: '1 1 auto', minWidth: 0 }}>{node.Name}</span>
                  <span style={pill}>{node.EntryType || node.Kind}</span>
                  {/* ★ The association, in the supplier's terms: "this is in the catalogue". */}
                  {node.CatalogueTitle && <span style={linkPill}>in the catalogue</span>}
                  <span style={tiny}>
                    {own.length > 0 ? `${own.length} ${own.length === 1 ? 'file' : 'files'}` : 'no files recorded'}
                  </span>
                </div>
                {isOpen && <Files files={own} />}
              </div>
            )
          })}
        </div>

        <p style={{ ...muted, marginTop: 12, maxWidth: 620 }}>
          Something missing or wrong?{' '}
          <a href="mailto:info@yoreflix.com" style={{ color: 'var(--primary)' }}>Let us know</a> and we will correct our
          records.
        </p>
      </div>
    </OverlayScrollArea>
  )
}
